/**
 * rubricaDeImagen — convertir la foto de una firma en una rúbrica estampable.
 *
 * Quien ya tiene su firma escaneada —o la foto de un papel hecha con el móvil—
 * no quiere volver a dibujarla con el dedo. Pero lo que sale de un escáner no
 * sirve tal cual: es un rectángulo blanco con un trazo dentro, y ese rectángulo
 * tapa la celda del acta donde cae, con sus bordes y su sombreado. Aquí se
 * arregla eso.
 *
 * ── Tres cosas que no son cosmética ─────────────────────────────────────
 *
 * **El blanco se vuelve transparente.** Sin esto, la firma se ve como un
 * parche de papel pegado encima de la tabla. Con alfa, solo queda el trazo.
 *
 * **Se recorta lo vacío.** Una foto de un papel es casi toda margen: si se
 * estampa entera, el trazo ocupa un tercio del recuadro y queda diminuto y
 * descentrado. Recortando hasta el trazo, la firma llena el hueco que le
 * señalaron.
 *
 * **Se reduce de tamaño.** Una foto de móvil son 12 megapíxeles. Estampada en
 * un recuadro de tres centímetros no se ve mejor, pero sí viaja por el correo,
 * se guarda en la base de datos y se mete en cada PDF de la ronda. El tope está
 * en lo que de verdad se imprime.
 *
 * El umbral no es 255. Un escaneo no tiene blancos puros: tiene 240, 247, 251,
 * y el ruido del sensor. Con 255 se quedaría todo el fondo opaco.
 */

/** Lo más que se guarda, en píxeles. Por encima no se nota al imprimir. */
export const TOPE_ANCHO = 900;
export const TOPE_ALTO = 300;

/** A partir de aquí un píxel se considera papel y no tinta. */
export const UMBRAL_BLANCO = 236;

/** Lo más que se acepta de un archivo, antes de abrirlo. */
export const TOPE_PESO = 6 * 1024 * 1024;

/**
 * Lo más que puede medir el base64 que se manda.
 *
 * El servidor rechaza por encima de 400 KB —`leerFirma`, en el router—, y con
 * razón: eso entra por el enlace público del correo y acaba guardado en la base
 * y metido en cada PDF. Aquí se deja algo de holgura por debajo de ese tope y
 * se encoge hasta cumplirlo, porque el fallo tiene que resolverlo esta pantalla
 * y no un error del servidor a la cara de quien solo quería firmar.
 *
 * Y se pasa de verdad: una foto con grano, ya recortada a 900×300, comprime mal
 * —el ruido no se repite, y eso es justo lo que un PNG no sabe comprimir—.
 */
export const TOPE_BASE64 = 360 * 1024;

/** Lo que se puede subir. PDF no: la celda del acta lleva una imagen. */
export const TIPOS = ['image/png', 'image/jpeg', 'image/webp'];

export function esImagenAdmitida(tipo) {
  return TIPOS.includes(String(tipo || '').toLowerCase());
}

/**
 * Cuánto hay que encoger para caber en el tope, sin deformar y sin agrandar.
 *
 * Nunca estira: una firma de 80 píxeles de ancho ampliada a 900 no gana
 * detalle, solo lo inventa borroso.
 */
export function encajar({ ancho, alto, topeAncho = TOPE_ANCHO, topeAlto = TOPE_ALTO }) {
  if (!(ancho > 0) || !(alto > 0)) return { ancho: 0, alto: 0, factor: 1 };
  const factor = Math.min(1, topeAncho / ancho, topeAlto / alto);
  return {
    ancho: Math.max(1, Math.round(ancho * factor)),
    alto: Math.max(1, Math.round(alto * factor)),
    factor
  };
}

/**
 * Deja transparente lo que es papel y oscurece lo que es tinta.
 *
 * Trabaja sobre el propio arreglo RGBA —cuatro bytes por píxel— porque es lo
 * que devuelve `getImageData`, y copiarlo para un retoque de este tamaño
 * duplicaría unos cuantos megabytes sin ninguna ganancia.
 */
export function aTransparente(datos, umbral = UMBRAL_BLANCO) {
  let tinta = 0;
  for (let i = 0; i < datos.length; i += 4) {
    const luz = (datos[i] + datos[i + 1] + datos[i + 2]) / 3;
    if (luz >= umbral) {
      datos[i + 3] = 0;
      continue;
    }
    tinta++;
    // El trazo se pasa a gris muy oscuro en vez de dejarlo como venía: la tinta
    // azul de un bolígrafo fotografiada con luz amarilla sale verdosa, y en el
    // acta —que es blanco y negro— eso se ve como una mancha, no como una firma.
    const tono = Math.round(luz * 0.35);
    datos[i] = tono; datos[i + 1] = tono; datos[i + 2] = tono;
    // Cuanto más claro el píxel, más translúcido: así el borde del trazo queda
    // suavizado en vez de con dientes de sierra.
    datos[i + 3] = Math.round(255 * Math.min(1, (umbral - luz) / 60));
  }
  return tinta;
}

/**
 * El rectángulo que de verdad ocupa el trazo, con un margen de holgura.
 *
 * Devuelve null cuando no hay nada opaco: una hoja en blanco, o una foto tan
 * clara que el umbral se la comió entera. Eso no es un recorte vacío, es un
 * «esto no vale», y quien llama tiene que poder distinguirlo.
 */
export function recorteDeTrazo(datos, ancho, alto, margen = 8) {
  let x1 = ancho, y1 = alto, x2 = -1, y2 = -1;
  for (let y = 0; y < alto; y++) {
    for (let x = 0; x < ancho; x++) {
      if (datos[(y * ancho + x) * 4 + 3] === 0) continue;
      if (x < x1) x1 = x;
      if (x > x2) x2 = x;
      if (y < y1) y1 = y;
      if (y > y2) y2 = y;
    }
  }
  if (x2 < 0) return null;

  x1 = Math.max(0, x1 - margen); y1 = Math.max(0, y1 - margen);
  x2 = Math.min(ancho - 1, x2 + margen); y2 = Math.min(alto - 1, y2 + margen);
  return { x: x1, y: y1, ancho: x2 - x1 + 1, alto: y2 - y1 + 1 };
}

/** Lo que se le dice a quien sube algo que no sirve. En su idioma, no en bytes. */
export function porQueNoVale(archivo) {
  if (!archivo) return 'No se recibió ningún archivo.';
  if (!esImagenAdmitida(archivo.type)) {
    return 'Tiene que ser una imagen (PNG, JPG o WEBP). Si tienes la firma en un '
      + 'PDF, haz una captura de pantalla de la firma y sube esa.';
  }
  if (archivo.size > TOPE_PESO) {
    const mb = (archivo.size / 1024 / 1024).toFixed(1);
    return `La imagen pesa ${mb} MB y el tope son ${TOPE_PESO / 1024 / 1024} MB. `
      + 'Recórtala a la zona de la firma y vuelve a intentarlo.';
  }
  return null;
}

/**
 * El siguiente tamaño al que intentarlo. Un cuarto menos cada vez: bajar de
 * diez en diez daría veinte reintentos, y cada uno vuelve a comprimir el PNG.
 */
export function siguienteAncho(ancho) {
  return Math.max(120, Math.round(ancho * 0.75));
}

/** Carga un archivo como imagen. Separado para poder probar el resto sin DOM. */
function abrirImagen(archivo) {
  return new Promise((listo, mal) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); listo(img); };
    // Revocar también al fallar: si no, el blob se queda en memoria hasta
    // recargar la página, y quien se equivoca de archivo lo intenta varias veces.
    img.onerror = () => { URL.revokeObjectURL(url); mal(new Error('imagen ilegible')); };
    img.src = url;
  });
}

/**
 * De un archivo a la rúbrica: PNG en base64 pelado, sin la cabecera `data:`,
 * igual que lo que devuelve el lienzo. Quien lo consume no debe notar por dónde
 * entró la firma.
 *
 * @throws {Error} con un mensaje ya escrito para leerse en pantalla.
 */
export async function rubricaDeArchivo(archivo) {
  const queja = porQueNoVale(archivo);
  if (queja) throw new Error(queja);

  const img = await abrirImagen(archivo).catch(() => {
    throw new Error('No se pudo leer la imagen. Prueba a guardarla como PNG o JPG.');
  });

  const cabida = encajar({ ancho: img.naturalWidth, alto: img.naturalHeight });
  if (!cabida.ancho) throw new Error('La imagen llegó vacía.');

  const lienzo = document.createElement('canvas');
  lienzo.width = cabida.ancho;
  lienzo.height = cabida.alto;
  const ctx = lienzo.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, cabida.ancho, cabida.alto);

  const pintura = ctx.getImageData(0, 0, cabida.ancho, cabida.alto);
  const tinta = aTransparente(pintura.data);
  if (!tinta) {
    throw new Error('No se encontró ningún trazo: la imagen es toda clara. '
      + 'Asegúrate de que la firma se ve bien y que no está muy desvaída.');
  }

  const corte = recorteDeTrazo(pintura.data, cabida.ancho, cabida.alto);
  ctx.putImageData(pintura, 0, 0);

  // ── Encoger hasta que quepa ─────────────────────────────────────────
  //
  // Se intenta con el recorte entero y, si el PNG sale por encima del tope, se
  // va reduciendo. Tres intentos como mucho: a partir de ahí la firma empezaría
  // a verse mal, y es mejor decirlo que estampar un borrón.
  let ancho = corte.ancho;
  let alto = corte.alto;
  for (let intento = 0; intento < 4; intento++) {
    const recortado = document.createElement('canvas');
    recortado.width = ancho;
    recortado.height = alto;
    recortado.getContext('2d').drawImage(
      lienzo, corte.x, corte.y, corte.ancho, corte.alto, 0, 0, ancho, alto
    );

    const base64 = recortado.toDataURL('image/png')
      .replace(/^data:image\/png;base64,/, '');
    if (base64.length <= TOPE_BASE64) return base64;

    const menos = siguienteAncho(ancho);
    alto = Math.max(1, Math.round(alto * (menos / ancho)));
    ancho = menos;
  }

  throw new Error('La imagen tiene demasiado detalle para usarla como firma. '
    + 'Recórtala a la zona del trazo —sin el resto del papel— y vuelve a subirla.');
}
